/**
 * Honest print preview — renders a photo as the FINAL print for a size + border,
 * shared by the Configure live preview and the wizard summary.
 *
 * WHITE BORDER (the "border must not crop" fix): the cropped image is SCALED TO
 * CONTAIN within the content area (print − 2·border), centred on a white
 * print-size rectangle. Nothing is cropped: the image gets smaller and white
 * makes up the difference (mirrors the bake exactly).
 * HANDLING BORDER: the print is unchanged; extra blank paper is shown around it.
 * EDGE / none: the image fills the print (crop) or is fitted with white (fit).
 *
 * ORIENTATION: follows the photo, except PANORAMA sizes (≥3:1) always render
 * wide — matching centeredAutoCropRect's pano rule.
 */

import { resolveBorder, type BorderMode, type PricingData } from '@rpl/pricing';
import type { CSSProperties } from 'react';
import { bwCssFilter } from '../../lib/bw';
import { isPanoInches } from '../../lib/printCheck';
import { cropRegionFracs, handlingBorderFrac, whiteBorderImageRect } from '../../lib/sizePaper';
import type { BwStyle, CropRect, FitMode } from '../../lib/types';

export interface PrintPreviewProps {
  photo: { previewUrl: string | null; width: number; height: number };
  sizeId: string;
  inches: [number, number];
  fitMode: FitMode;
  cropRect: CropRect | null;
  borderMode: BorderMode;
  /** Custom white-border width (owner 2026-08-01): overrides the pricing
   *  default so a saved custom width previews at its true proportion. */
  borderMm?: number | null;
  pricing: PricingData;
  /** Black & white print — renders the photo grayscale (round-11 fix: the
   * editor's B&W choice must show on every preview surface). */
  bw?: boolean;
  bwStyle?: BwStyle | null;
  bwStrength?: number | null;
  className?: string;
}

/** Style that maps the crop region of the image onto its wrapper (contain for fit). */
function cropImgStyle(cropRect: CropRect | null, fitMode: FitMode): CSSProperties {
  if (fitMode === 'fit') return { width: '100%', height: '100%', objectFit: 'contain' };
  if (!cropRect) return { width: '100%', height: '100%', objectFit: 'cover' };
  const f = cropRegionFracs(cropRect);
  return {
    position: 'absolute',
    width: `${f.width * 100}%`,
    height: `${f.height * 100}%`,
    left: `${f.left * 100}%`,
    top: `${f.top * 100}%`,
    maxWidth: 'none',
    objectFit: 'cover',
  };
}

export function PrintPreview({
  photo,
  sizeId,
  inches,
  fitMode,
  cropRect,
  borderMode,
  borderMm = null,
  pricing,
  bw = false,
  bwStyle = null,
  bwStrength = null,
  className,
}: PrintPreviewProps) {
  // Panorama sizes always preview wide (the auto-crop takes a wide band).
  const landscape = isPanoInches(inches)
    ? true
    : photo.width && photo.height
      ? photo.width >= photo.height
      : inches[0] >= inches[1];
  const lo = Math.min(inches[0], inches[1]);
  const hi = Math.max(inches[0], inches[1]);
  const printWin = landscape ? hi : lo;
  const printHin = landscape ? lo : hi;

  const resolved = borderMode === 'none' ? null : resolveBorder(pricing, sizeId, borderMode);
  const border =
    resolved && borderMode === 'white' && borderMm != null
      ? { ...resolved, mm: borderMm }
      : resolved;
  const imgStyle = cropImgStyle(cropRect, fitMode);
  const img = photo.previewUrl ? (
    <img
      src={photo.previewUrl}
      alt=""
      draggable={false}
      style={{ ...imgStyle, ...(bw ? { filter: bwCssFilter(bw, bwStyle, bwStrength) } : {}) }}
    />
  ) : (
    <span className="ppreview__ph" />
  );
  const cls = `ppreview${bw && (!bwStyle || bwStyle === 'v1') ? ' ppreview--bw' : ''}${className ? ` ${className}` : ''}`;

  /**
   * Style with the box aspect ratio + a `--ppreview-ar` custom property (w/h)
   * so CSS can cap the WIDTH from a height budget (round-4 bounding fix:
   * `max-width: calc(<maxH> * var(--ppreview-ar))` letterboxes extreme
   * verticals INSIDE their stage instead of stretching the pane).
   */
  const arStyle = (w: number, h: number): CSSProperties =>
    ({ aspectRatio: `${w} / ${h}`, '--ppreview-ar': String(w / h) }) as CSSProperties;

  // HANDLING (outset): extra paper around an unchanged print.
  if (border?.geometry === 'outset') {
    const frac = handlingBorderFrac(printWin, printHin, border.mm);
    const bIn = border.mm / 25.4;
    return (
      <div
        className={`${cls} ppreview--handling`}
        style={arStyle(printWin + 2 * bIn, printHin + 2 * bIn)}
        aria-hidden
      >
        <div
          className="ppreview__print"
          style={{
            left: `${frac.x * 100}%`,
            right: `${frac.x * 100}%`,
            top: `${frac.y * 100}%`,
            bottom: `${frac.y * 100}%`,
          }}
        >
          <span className="ppreview__imgwrap ppreview__imgwrap--full">{img}</span>
        </div>
      </div>
    );
  }

  // WHITE (inset): image scaled to contain within the content area, white around.
  if (border?.geometry === 'inset') {
    const r = whiteBorderImageRect(printWin, printHin, border.mm);
    // PREVIEW-ONLY minimum: a true-to-scale 4mm border on a large print is a
    // fraction of a percent — sub-pixel at preview size, so the border edges
    // "disappear" (staff round: large custom + white border looked cut off).
    // Clamp each margin to a visible floor; the bake stays exact.
    const MIN_FRAC = 0.02;
    const x = Math.max(r.x, MIN_FRAC);
    const y = Math.max(r.y, MIN_FRAC);
    const w = Math.min(r.w, 1 - 2 * x);
    const h = Math.min(r.h, 1 - 2 * y);
    return (
      <div className={`${cls} ppreview--white`} style={arStyle(printWin, printHin)} aria-hidden>
        <span
          className="ppreview__imgwrap"
          style={{ left: `${x * 100}%`, top: `${y * 100}%`, width: `${w * 100}%`, height: `${h * 100}%` }}
        >
          {img}
        </span>
      </div>
    );
  }

  // EDGE / none.
  return (
    <div className={`${cls} ppreview--edge`} style={arStyle(printWin, printHin)} aria-hidden>
      <span className="ppreview__imgwrap ppreview__imgwrap--full">{img}</span>
    </div>
  );
}
